/**
 * A label's runs (`TextRun`) worked on: joined where they look alike, a stretch of them given a look,
 * and what look a stretch has — for the field the inspector types a label's words in (ui/richtext.ts).
 */
import type { TextRun } from '../../shared/doc.ts';

export type Look = 'b' | 'i' | 'u' | 's';


/** Runs joined where they look alike (nothing trimmed, nothing cut: as typed). */
export function mergeRuns(runs: readonly TextRun[]): TextRun[] {
  const out: TextRun[] = [];
  for (const r of runs) {
    if (!r.t) continue;
    const last = out[out.length - 1];
    if (last && alike(last, r)) out[out.length - 1] = { ...last, t: last.t + r.t };
    else out.push({ ...r });
  }
  return out;
}
const alike = (a: TextRun, b: TextRun) => !!a.b === !!b.b && !!a.i === !!b.i && !!a.u === !!b.u && !!a.s === !!b.s && (a.c ?? '') === (b.c ?? '');

/** Runs with a stretch of their words (`from`…`to`, in characters) given a look by `fn`. */
export function restyle(runs: readonly TextRun[], from: number, to: number, fn: (r: TextRun) => TextRun): TextRun[] {
  const out: TextRun[] = [];
  let at = 0;
  for (const r of runs) {
    const a = at,
      b = at + r.t.length;
    at = b;
    if (b <= from || a >= to) {
      out.push(r);
      continue;
    }
    const s = Math.max(from, a) - a,
      e = Math.min(to, b) - a;
    if (s > 0) out.push({ ...r, t: r.t.slice(0, s) });
    out.push(fn({ ...r, t: r.t.slice(s, e) }));
    if (e < r.t.length) out.push({ ...r, t: r.t.slice(e) });
  }
  return mergeRuns(out);
}
/** Whether every character of a stretch (or, an empty one, the one before it) has a look. */
export function allHave(runs: readonly TextRun[], from: number, to: number, k: Look): boolean {
  let at = 0;
  let seen = false;
  const lo = from === to ? Math.max(0, from - 1) : from;
  const hi = from === to ? Math.max(1, from) : to;
  for (const r of runs) {
    const a = at,
      b = at + r.t.length;
    at = b;
    if (b <= lo || a >= hi) continue;
    // (spaces and line breaks say nothing of a look)
    const part = r.t.slice(Math.max(lo, a) - a, Math.min(hi, b) - a);
    if (!part.trim()) continue;
    seen = true;
    if (!r[k]) return false;
  }
  return seen;
}
/** The one colour of a stretch (null: its own, or more than one). */
export function colourOf(runs: readonly TextRun[], from: number, to: number): string | null {
  let at = 0;
  let c: string | null | undefined;
  const lo = from === to ? Math.max(0, from - 1) : from;
  const hi = from === to ? Math.max(1, from) : to;
  for (const r of runs) {
    const a = at,
      b = at + r.t.length;
    at = b;
    if (b <= lo || a >= hi || !r.t.trim()) continue;
    const own = r.c ?? null;
    if (c === undefined) c = own;
    else if (c !== own) return null;
  }
  return c ?? null;
}

